// Thin axios wrappers around the additive /api/chats/* surface.
// Cookies flow automatically (axios.defaults.withCredentials=true is set
// by authContext on app boot).

import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Fetch per-chat contacts. Returns { phone_number, email_address }. */
export async function getChatContacts(chatId) {
    if (!chatId) return { phone_number: null, email_address: null };
    try {
        const { data } = await axios.get(`${API}/chats/${encodeURIComponent(chatId)}/contacts`);
        return {
            phone_number: data?.phone_number || null,
            email_address: data?.email_address || null,
        };
    } catch (_) {
        return { phone_number: null, email_address: null };
    }
}

/** Upsert per-chat contacts. Returns the persisted record. */
export async function saveChatContacts(chatId, { phone_number, email_address }) {
    if (!chatId) return null;
    const { data } = await axios.put(`${API}/chats/${encodeURIComponent(chatId)}/contacts`, {
        phone_number: phone_number ?? null,
        email_address: email_address ?? null,
    });
    return data;
}

/**
 * Log a send-event to MongoDB. Fire-and-forget — the UI MUST not block on
 * this. Swallows network errors so the user's WhatsApp / Gmail / copy
 * action always feels instant.
 */
export function logSendEvent(payload) {
    try {
        axios.post(`${API}/chats/send-events`, payload).catch(() => {});
    } catch (_) {
        /* never throw */
    }
}

/** Strip everything except digits + leading + for wa.me URLs. */
export function normalizeWhatsAppNumber(raw) {
    if (!raw) return null;
    // Keep leading + if present, then digits only.
    const trimmed = String(raw).trim();
    if (!trimmed) return null;
    const digits = trimmed.replace(/[^\d]/g, "");
    if (digits.length < 6) return null; // sanity guard
    return digits;
}

// ---------- WhatsApp + Gmail deep links ----------

/**
 * Build a WhatsApp deep link per spec — `https://wa.me/<digits>?text=<encoded>`.
 *
 * One URL format for every device. No User-Agent detection, no hybrid
 * fallback. Mobile platforms resolve wa.me as a universal/app link to the
 * native WhatsApp app; desktop browsers follow WhatsApp's own redirect.
 * If the destination is blocked by the user's network or browser, the
 * always-visible Copy button is the documented fallback per spec.
 */
export function buildWhatsAppLink(phoneNumber, text) {
    const num = normalizeWhatsAppNumber(phoneNumber);
    if (!num) return null;
    const body = encodeURIComponent(text || "");
    return `https://wa.me/${num}?text=${body}`;
}

/** Build a mailto link for Gmail / any default mail handler. */
export function buildMailtoLink(email, text) {
    if (!email) return null;
    const body = encodeURIComponent(text || "");
    return `mailto:${encodeURIComponent(email)}?body=${body}`;
}
